import dayjs from 'dayjs';
import { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as ErrorInfoIcon } from 'assett/icons/error_info_icon.svg';
import { ReactComponent as InfoIcon } from 'assett/icons/info_icon.svg';
import { handleChange } from 'feature/form/formSlice';
import {
	CARRIER_DATE_OFFSET,
	useCheckEligibility,
} from 'hooks/useCarrierCustomization';
import { RootState } from 'store/store';
import { FormControl, Typography } from 'styles';
import DatePicker from 'styles/datePicker/DatePicker';
import Stack from 'styles/stack/Stack';
import palette from 'styles/theme/Palette';
import { ECarriersList, IClient } from 'types/Carrier';
import { useTranslation } from 'utils/translations';

const DateOfService: FC<{ selectedClient?: IClient }> = ({
	selectedClient,
}) => {
	const dispatch = useDispatch();
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const { dateOfService, isDateOfServiceValid } = useSelector(
		(store: RootState) => store.form,
	);

	const { showDateInfoLabel, checkEligibilityForceDate } = useCheckEligibility();

	const handleChangeDate = useCallback(
		(value: dayjs.Dayjs | null) => {
			dispatch(
				handleChange<any>({
					name: 'dateOfService',
					value: value,
				}),
			);
			dispatch(
				handleChange<any>({
					name: 'isDateOfServiceValid',
					value: true,
				}),
			);
		},
		[dispatch],
	);

	useEffect(() => {
		if (checkEligibilityForceDate(selectedClient)) {
			handleChangeDate(dayjs());
			dispatch(
				handleChange<any>({
					name: 'isDateOfServiceValid',
					value: true,
				}),
			);
		}
	}, [checkEligibilityForceDate, dispatch, handleChangeDate, selectedClient]);

	useEffect(() => {
		handleChangeDate(dayjs());
	}, [dispatch, handleChangeDate]);

	return (
		<Stack sx={{ rowGap: 1 }}>
			<FormControl sx={{ width: { sm: 340, lg: 368 } }}>
				<DatePicker
					variant="margin"
					label={`${tClientLabels('check-and-submit.dateOfService')}${
						!checkEligibilityForceDate(selectedClient) ? '*' : ''
					}`}
					value={dateOfService}
					onChange={value => handleChangeDate(value)}
					maxDate={dayjs()}
					error={!isDateOfServiceValid}
					disabled={checkEligibilityForceDate(selectedClient)}
				/>
			</FormControl>
			{showDateInfoLabel(selectedClient) && (
				<Stack flexDirection={'row'} alignItems={'center'}>
					{!isDateOfServiceValid ? (
						<ErrorInfoIcon style={{ marginRight: '8px' }} />
					) : (
						<InfoIcon style={{ marginRight: '8px' }} />
					)}
					<Typography
						variant="caption"
						sx={{ color: !isDateOfServiceValid ? palette.error.main : 'inherit' }}
					>
						{tClientLabels('check-and-submit.errors.dateOfService', {
							dateoffset:
								CARRIER_DATE_OFFSET[
									selectedClient?.carrier.customLayout || ECarriersList.DEFAULT_CARRIER
								],
						})}
					</Typography>
				</Stack>
			)}
		</Stack>
	);
};

export default DateOfService;
